export type IncomingCallAction = 'answer' | 'reject';

export interface IncomingCallLink {
  callId: number;
  storeId: number | null;
  action: IncomingCallAction | null;
  provider: 'whatsapp' | 'voip' | null;
}

export const INCOMING_CALL_STORAGE_KEY = 'zeedcom_incoming_call';

export const parseIncomingCallSearch = (search: string): IncomingCallLink | null => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const callId = Number(params.get('call'));
  if (!Number.isFinite(callId) || callId <= 0) return null;
  const storeId = Number(params.get('store'));
  const raw = params.get('action');
  const action = raw === 'answer' || raw === 'reject' ? raw : null;
  const providerRaw = params.get('provider');
  const provider = providerRaw === 'voip' ? 'voip' : providerRaw === 'whatsapp' ? 'whatsapp' : null;
  return {
    callId,
    storeId: Number.isFinite(storeId) && storeId > 0 ? storeId : null,
    action,
    provider
  };
};

export const persistIncomingCallLink = (link: IncomingCallLink) => {
  try {
    const raw = JSON.stringify(link);
    sessionStorage.setItem(INCOMING_CALL_STORAGE_KEY, raw);
    localStorage.setItem(INCOMING_CALL_STORAGE_KEY, raw);
  } catch {
    // Private mode can block storage.
  }
};

export const readStoredIncomingCallLink = (): IncomingCallLink | null => {
  try {
    const raw = sessionStorage.getItem(INCOMING_CALL_STORAGE_KEY)
      || localStorage.getItem(INCOMING_CALL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IncomingCallLink;
    if (!parsed || !Number.isFinite(parsed.callId) || parsed.callId <= 0) return null;
    const action = parsed.action === 'answer' || parsed.action === 'reject' ? parsed.action : null;
    const storeId = Number(parsed.storeId);
    const provider = parsed.provider === 'voip'
      ? 'voip'
      : parsed.provider === 'whatsapp'
        ? 'whatsapp'
        : null;
    return {
      callId: parsed.callId,
      storeId: Number.isFinite(storeId) && storeId > 0 ? storeId : null,
      action,
      provider
    };
  } catch {
    return null;
  }
};

export const peekIncomingCallLink = (): IncomingCallLink | null => {
  if (typeof window === 'undefined') return null;
  const fromUrl = parseIncomingCallSearch(window.location.search);
  if (fromUrl) {
    persistIncomingCallLink(fromUrl);
    return fromUrl;
  }
  return readStoredIncomingCallLink();
};

export const captureIncomingCallFromLocation = (): IncomingCallLink | null => (
  peekIncomingCallLink()
);

export const clearIncomingCallLink = () => {
  try {
    sessionStorage.removeItem(INCOMING_CALL_STORAGE_KEY);
    localStorage.removeItem(INCOMING_CALL_STORAGE_KEY);
  } catch {
    // Private mode can block storage.
  }
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('call')) return;
  url.searchParams.delete('call');
  url.searchParams.delete('store');
  url.searchParams.delete('action');
  url.searchParams.delete('provider');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
};
